import { ENVELOPE_PRESETS } from '../av/envelope';
import { sessionTimeline } from '../av/events';
import { TICKS_PER_BAR, TICKS_PER_BEAT, ticksToSeconds } from '../av/time';
import { TEMPLATES } from '../animation/procedural/spec';
import { createId } from '../lib/ids';
import { animationOf } from './animationOps';
import { applyMotionPlan, type ConflictPolicy } from './motionOps';
import { OpError } from './projectOps';
import {
  AV_MAX_TICK,
  AvMappingSchema,
  AvSessionSchema,
  MAX_AV_MAPPINGS,
  MAX_AV_MARKERS,
  MAX_AV_MOTION_CUES,
  MAX_AV_SESSIONS,
  SCENE_REACTIONS,
  formatIssues,
  type AvMapping,
  type AvMotionCue,
  type AvSession,
  type EventCategory,
  type Project,
  type ReactionType,
} from './schemas';

/**
 * Pure, validated operations on audio-visual sessions (Phase 4A). Each returns a new
 * Project or throws OpError and changes nothing. Sessions hold references and settings
 * only; they never modify the music arrangement or an animation's keyframes — except
 * `applyMotionCues`, which explicitly compiles motion cues into ordinary keyframes through
 * the Phase 3C compiler (one change, one undo step).
 *
 * Reference policy (see `pruneSessions`): deleting a scene deletes its sessions; deleting
 * an animation unlinks it (the session shows the base scene); deleting an object removes
 * the mappings and motion cues that target it. Music references are soft: a missing
 * pattern source plays the song, a mapping limited to a missing section matches nothing.
 */

export function sessionOf(project: Project, sessionId: string): AvSession {
  const s = project.avSessions.find((x) => x.id === sessionId);
  if (!s) throw new OpError('That audio-visual session no longer exists.');
  return s;
}

/** Checks a session against the project (strict references) — the same rules as ProjectSchema. */
function checkRefs(project: Project, s: AvSession): void {
  const scene = project.scenes.find((x) => x.id === s.sceneId);
  if (!scene) throw new OpError('The session’s scene no longer exists.');
  if (s.animationId !== null) {
    const a = project.animations.find((x) => x.id === s.animationId);
    if (!a) throw new OpError('The session’s animation no longer exists.');
    if (a.sceneId !== s.sceneId) throw new OpError(`“${a.name}” belongs to another scene.`);
  }
  const objects = new Map(scene.objects.map((o) => [o.id, o]));
  for (const m of s.mappings) {
    if (m.target.kind !== 'object') continue;
    const o = objects.get(m.target.objectId);
    if (!o) throw new OpError(`“${m.name}” targets an object that is not in “${scene.name}”.`);
    if (m.reaction === 'pose' && o.type !== 'character') throw new OpError(`Pose reactions need a character (“${o.name}” is a ${o.type}).`);
  }
  for (const c of s.motionCues) {
    const o = objects.get(c.objectId);
    if (!o) throw new OpError(`A motion cue targets an object that is not in “${scene.name}”.`);
    if (TEMPLATES[c.template].characterOnly && o.type !== 'character') throw new OpError(`${TEMPLATES[c.template].label} needs a character (“${o.name}” is a ${o.type}).`);
  }
}

/** Validates a session (schema + strict references) and puts it into the project. */
export function putSession(project: Project, s: AvSession, isNew = false): Project {
  const parsed = AvSessionSchema.safeParse(s);
  if (!parsed.success) throw new OpError(`That change would make the session invalid: ${formatIssues(parsed.error)[0] ?? 'unknown problem'}`);
  checkRefs(project, parsed.data);
  if (isNew) {
    if (project.avSessions.length >= MAX_AV_SESSIONS) throw new OpError(`A project can hold at most ${MAX_AV_SESSIONS} audio-visual sessions.`);
    if (project.avSessions.some((x) => x.id === s.id)) throw new OpError('A session with that id already exists.');
    return { ...project, avSessions: [...project.avSessions, parsed.data] };
  }
  sessionOf(project, s.id);
  return { ...project, avSessions: project.avSessions.map((x) => (x.id === s.id ? parsed.data : x)) };
}

/** Length of a session's music in ticks. */
export const musicTicks = (project: Project, s: Pick<AvSession, 'music'>): number => sessionTimeline(project, s.music).totalTicks;

/** Session length in seconds per its duration mode. */
export function sessionDuration(project: Project, s: AvSession): number {
  const bpm = project.settings.bpm;
  if (s.durationMode === 'custom' && s.customSeconds !== null) return s.customSeconds;
  if (s.durationMode === 'animation' && s.animationId) {
    const a = project.animations.find((x) => x.id === s.animationId);
    if (a) return ticksToSeconds(s.animationStartTick, bpm) + a.durationFrames / a.fps;
  }
  return ticksToSeconds(musicTicks(project, s), bpm);
}

export interface NewSession {
  name?: string;
  sceneId?: string;
  animationId?: string | null;
  music?: AvSession['music'];
}

export function createSession(project: Project, options: NewSession = {}, id = createId('avs')): { project: Project; id: string } {
  const sceneId = options.sceneId ?? project.activeSceneId;
  const animationId = options.animationId !== undefined ? options.animationId : (project.animations.find((a) => a.sceneId === sceneId)?.id ?? null);
  const music = options.music ?? { kind: 'song' as const };
  const total = Math.max(TICKS_PER_BAR, musicTicks(project, { music }));
  const taken = new Set(project.avSessions.map((s) => s.name));
  let name = options.name?.trim().slice(0, 60) || 'Sync 1';
  if (!options.name?.trim()) for (let n = 1; taken.has(name); n++) name = `Sync ${n + 1}`;
  const session: AvSession = {
    id,
    name,
    version: 1,
    music,
    sceneId,
    animationId,
    animationStartTick: 0,
    loop: { enabled: false, startTick: 0, endTick: Math.min(AV_MAX_TICK, total) },
    endBehavior: 'stop',
    durationMode: 'music',
    customSeconds: null,
    muted: false,
    markers: [],
    mappings: [],
    motionCues: [],
    visualizer: { layers: [] },
  };
  return { project: putSession(project, session, true), id };
}

export type SessionPatch = Partial<Pick<AvSession, 'name' | 'music' | 'sceneId' | 'animationId' | 'animationStartTick' | 'endBehavior' | 'durationMode' | 'customSeconds' | 'muted'>>;

/** Changes session settings. Changing the scene drops mappings and cues for objects not in the new scene. */
export function updateSession(project: Project, sessionId: string, patch: SessionPatch): Project {
  const s = sessionOf(project, sessionId);
  let next: AvSession = { ...s, ...patch };
  if (patch.sceneId !== undefined && patch.sceneId !== s.sceneId) {
    const scene = project.scenes.find((x) => x.id === patch.sceneId);
    if (!scene) throw new OpError('That scene no longer exists.');
    const ids = new Set(scene.objects.map((o) => o.id));
    next = {
      ...next,
      animationId: patch.animationId !== undefined ? patch.animationId : (project.animations.find((a) => a.sceneId === scene.id)?.id ?? null),
      mappings: next.mappings.filter((m) => m.target.kind === 'scene' || ids.has(m.target.objectId)),
      motionCues: next.motionCues.filter((c) => ids.has(c.objectId)),
    };
  }
  if (patch.music) {
    const total = Math.max(TICKS_PER_BAR, musicTicks(project, next));
    if (next.loop.endTick > total) next = { ...next, loop: { ...next.loop, startTick: Math.min(next.loop.startTick, total - TICKS_PER_BAR), endTick: total } };
  }
  return putSession(project, next);
}

export function renameSession(project: Project, sessionId: string, name: string): Project {
  if (!name.trim()) throw new OpError('A session name cannot be empty.');
  return updateSession(project, sessionId, { name: name.trim().slice(0, 60) });
}

export function deleteSession(project: Project, sessionId: string): Project {
  sessionOf(project, sessionId);
  return { ...project, avSessions: project.avSessions.filter((s) => s.id !== sessionId) };
}

// ---------------------------------------------------------------- Loop

export function setLoop(project: Project, sessionId: string, loop: Partial<AvSession['loop']>): Project {
  const s = sessionOf(project, sessionId);
  const next = { ...s.loop, ...loop };
  if (next.endTick <= next.startTick) throw new OpError('The loop must end after it starts.');
  return putSession(project, { ...s, loop: next });
}

/** Loop whole bars (1-based, inclusive): "loop bars 1 through 8". */
export function loopBars(project: Project, sessionId: string, fromBar: number, toBar: number): Project {
  if (!Number.isInteger(fromBar) || !Number.isInteger(toBar) || fromBar < 1 || toBar < fromBar) throw new OpError('Loop whole bars: from bar 1 or later, to the same bar or later.');
  const s = sessionOf(project, sessionId);
  const total = musicTicks(project, s);
  const endTick = toBar * TICKS_PER_BAR;
  if (endTick > total) throw new OpError(`The music has ${Math.round(total / TICKS_PER_BAR)} bars.`);
  return setLoop(project, sessionId, { enabled: true, startTick: (fromBar - 1) * TICKS_PER_BAR, endTick });
}

// ---------------------------------------------------------------- Mappings

const REACTION_DEFAULTS: Record<ReactionType, AvMapping['params']> = {
  opacity: { amount: -0.6 },
  scale: { amount: 0.18 },
  position: { amount: 24, direction: 'up' },
  rotation: { amount: 12 },
  tint: { amount: 0.8, color: '#f472b6' },
  pose: { amount: 1, pose: 'arms-up' },
  visibility: { amount: 1, mode: 'toggle' },
  flash: { amount: 0.5, color: '#ffffff' },
  background: { amount: 0.35, color: '#a78bfa' },
  shake: { amount: 8 },
};

export const REACTION_LABELS: Record<ReactionType, string> = {
  opacity: 'Opacity pulse',
  scale: 'Scale pulse',
  position: 'Bounce',
  rotation: 'Rotation impulse',
  tint: 'Colour pulse',
  pose: 'Pose change',
  visibility: 'Visibility',
  flash: 'Screen flash',
  background: 'Background pulse',
  shake: 'Camera shake',
};

export const CATEGORY_LABELS: Record<EventCategory, string> = {
  kick: 'Kick',
  snare: 'Snare',
  clap: 'Clap',
  hats: 'Hi-hats',
  perc: 'Percussion',
  cowbell: 'Cowbell',
  bass808: '808',
  lead: 'Lead',
  pad: 'Pad',
  beat: 'Beat',
  bar: 'Bar',
  section: 'Section start',
  clip: 'Clip start',
  manual: 'Manual marker',
};

/** A complete mapping with sensible defaults (UI and prompts). */
export function defaultMapping(reaction: ReactionType, categories: EventCategory[], objectId: string | null, id = createId('avm')): AvMapping {
  const scene = (SCENE_REACTIONS as readonly string[]).includes(reaction);
  return {
    id,
    name: `${REACTION_LABELS[reaction]} on ${categories.map((c) => CATEGORY_LABELS[c].toLowerCase()).join(' + ')}`.slice(0, 60),
    enabled: true,
    source: { categories, every: 1, phase: 0, minVelocity: 0, sectionId: null },
    target: scene || !objectId ? { kind: 'scene' } : { kind: 'object', objectId },
    reaction,
    params: { ...REACTION_DEFAULTS[reaction] },
    envelope: { ...(reaction === 'position' ? ENVELOPE_PRESETS.smooth : reaction === 'shake' ? { ...ENVELOPE_PRESETS.instant, decay: 0.2 } : ENVELOPE_PRESETS.instant) },
    offsetBeats: 0,
    quantize: 0,
    cooldownBeats: 0,
    velocitySensitivity: 0.5,
    seed: 1,
  };
}

export function addMapping(project: Project, sessionId: string, mapping: AvMapping): Project {
  const s = sessionOf(project, sessionId);
  if (s.mappings.length >= MAX_AV_MAPPINGS) throw new OpError(`A session can hold at most ${MAX_AV_MAPPINGS} reactions.`);
  const parsed = AvMappingSchema.safeParse(mapping);
  if (!parsed.success) throw new OpError(`That reaction is invalid: ${formatIssues(parsed.error)[0] ?? 'unknown problem'}`);
  return putSession(project, { ...s, mappings: [...s.mappings, parsed.data] });
}

export function updateMapping(project: Project, sessionId: string, mappingId: string, patch: Partial<Omit<AvMapping, 'id'>>): Project {
  const s = sessionOf(project, sessionId);
  if (!s.mappings.some((m) => m.id === mappingId)) throw new OpError('That reaction no longer exists.');
  return putSession(project, { ...s, mappings: s.mappings.map((m) => (m.id === mappingId ? { ...m, ...patch } : m)) });
}

export function removeMapping(project: Project, sessionId: string, mappingId: string): Project {
  const s = sessionOf(project, sessionId);
  if (!s.mappings.some((m) => m.id === mappingId)) throw new OpError('That reaction no longer exists.');
  return putSession(project, { ...s, mappings: s.mappings.filter((m) => m.id !== mappingId) });
}

export function duplicateMapping(project: Project, sessionId: string, mappingId: string, id = createId('avm')): Project {
  const s = sessionOf(project, sessionId);
  const m = s.mappings.find((x) => x.id === mappingId);
  if (!m) throw new OpError('That reaction no longer exists.');
  return addMapping(project, sessionId, { ...structuredClone(m), id, name: `${m.name} copy`.slice(0, 60) });
}

/** Scales a mapping's strength (prompt "increase the pulse strength"), clamped to the schema. */
export function scaleMappingStrength(project: Project, sessionId: string, mappingId: string, factor: number): Project {
  const m = sessionOf(project, sessionId).mappings.find((x) => x.id === mappingId);
  if (!m) throw new OpError('That reaction no longer exists.');
  const limit = m.reaction === 'position' ? 2000 : m.reaction === 'rotation' ? 360 : m.reaction === 'shake' ? 100 : ['opacity', 'tint', 'flash', 'background'].includes(m.reaction) ? 1 : 5;
  const amount = Math.round(Math.max(-limit, Math.min(limit, m.params.amount * factor)) * 1000) / 1000;
  return updateMapping(project, sessionId, mappingId, { params: { ...m.params, amount } });
}

// ---------------------------------------------------------------- Markers

export function addMarker(project: Project, sessionId: string, tick: number, label = '', id = createId('avk')): { project: Project; id: string } {
  const s = sessionOf(project, sessionId);
  if (s.markers.length >= MAX_AV_MARKERS) throw new OpError(`A session can hold at most ${MAX_AV_MARKERS} manual markers.`);
  const t = Math.round(tick);
  if (s.markers.some((m) => m.tick === t)) throw new OpError('There is already a marker at that position.');
  const markers = [...s.markers, { id, tick: t, label: label.trim().slice(0, 40) }].sort((a, b) => a.tick - b.tick);
  return { project: putSession(project, { ...s, markers }), id };
}

/** Moves a manual marker to another position (ticks; snap to beats or frames in the caller). */
export function moveMarker(project: Project, sessionId: string, markerId: string, tick: number): Project {
  const s = sessionOf(project, sessionId);
  if (!s.markers.some((m) => m.id === markerId)) throw new OpError('That marker no longer exists.');
  const t = Math.round(tick);
  if (s.markers.some((m) => m.tick === t && m.id !== markerId)) throw new OpError('There is already a marker at that position.');
  return putSession(project, { ...s, markers: s.markers.map((m) => (m.id === markerId ? { ...m, tick: t } : m)).sort((a, b) => a.tick - b.tick) });
}

export function renameMarker(project: Project, sessionId: string, markerId: string, label: string): Project {
  const s = sessionOf(project, sessionId);
  if (!s.markers.some((m) => m.id === markerId)) throw new OpError('That marker no longer exists.');
  return putSession(project, { ...s, markers: s.markers.map((m) => (m.id === markerId ? { ...m, label: label.trim().slice(0, 40) } : m)) });
}

export function removeMarker(project: Project, sessionId: string, markerId: string): Project {
  const s = sessionOf(project, sessionId);
  if (!s.markers.some((m) => m.id === markerId)) throw new OpError('That marker no longer exists.');
  return putSession(project, { ...s, markers: s.markers.filter((m) => m.id !== markerId) });
}

// ---------------------------------------------------------------- Motion cues (Phase 3C on the musical timeline)

export function addMotionCue(project: Project, sessionId: string, cue: Omit<AvMotionCue, 'id'>, id = createId('avc')): { project: Project; id: string } {
  const s = sessionOf(project, sessionId);
  if (s.motionCues.length >= MAX_AV_MOTION_CUES) throw new OpError(`A session can hold at most ${MAX_AV_MOTION_CUES} motion cues.`);
  return { project: putSession(project, { ...s, motionCues: [...s.motionCues, { ...cue, id }] }), id };
}

export function updateMotionCue(project: Project, sessionId: string, cueId: string, patch: Partial<Omit<AvMotionCue, 'id'>>): Project {
  const s = sessionOf(project, sessionId);
  if (!s.motionCues.some((c) => c.id === cueId)) throw new OpError('That motion cue no longer exists.');
  return putSession(project, { ...s, motionCues: s.motionCues.map((c) => (c.id === cueId ? { ...c, ...patch } : c)) });
}

export function removeMotionCue(project: Project, sessionId: string, cueId: string): Project {
  const s = sessionOf(project, sessionId);
  if (!s.motionCues.some((c) => c.id === cueId)) throw new OpError('That motion cue no longer exists.');
  return putSession(project, { ...s, motionCues: s.motionCues.filter((c) => c.id !== cueId) });
}

/** Moves a motion cue to another musical position ("move the jump to beat 16"). */
export function moveMotionCue(project: Project, sessionId: string, cueId: string, tick: number): Project {
  return updateMotionCue(project, sessionId, cueId, { tick: Math.max(0, Math.round(tick)) });
}

/**
 * Bakes a session's motion cues into ordinary keyframes of its animation through the
 * Phase 3C compiler (never during playback). Each occurrence becomes a step at its musical
 * time (minus the animation's start). The baked cues are removed from the session (they
 * are now permanent keyframes, no longer overlays). The conflict policy is explicit; one
 * call is one project change (one undo step).
 */
export function applyMotionCues(project: Project, sessionId: string, policy: ConflictPolicy = 'merge', allowExtend = false, newId?: (prefix: string) => string): { project: Project; summary: string; animationId: string } {
  const s = sessionOf(project, sessionId);
  if (!s.motionCues.length) throw new OpError('This session has no motion cues to apply.');
  const bpm = project.settings.bpm;
  const start = ticksToSeconds(s.animationStartTick, bpm);
  const scene = project.scenes.find((x) => x.id === s.sceneId)!;
  const slots: { name: string; kind: 'character' | 'object'; label: string }[] = [];
  const binding: Record<string, string> = {};
  const steps = [];
  for (const cue of s.motionCues) {
    const o = scene.objects.find((x) => x.id === cue.objectId)!;
    const slot = `o-${o.id.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`.slice(0, 32).replace(/-+$/, '') || 'o-x';
    if (!binding[slot]) {
      binding[slot] = o.id;
      slots.push({ name: slot, kind: o.type === 'character' ? 'character' : 'object', label: o.name });
    }
    for (let i = 0; i < cue.count; i++) {
      const tick = cue.tick + i * (cue.everyTicks ?? 0);
      const at = ticksToSeconds(tick, bpm) - start;
      if (at < 0) throw new OpError(`A ${TEMPLATES[cue.template].label.toLowerCase()} cue at bar ${Math.floor(tick / TICKS_PER_BAR) + 1} is before the animation starts.`);
      steps.push({
        id: `c${steps.length + 1}`,
        template: cue.template,
        targets: [slot],
        timing: { mode: 'at' as const, at: Math.round(at * 10000) / 10000 },
        ...(cue.durationBeats !== null ? { duration: ticksToSeconds(cue.durationBeats * TICKS_PER_BEAT, bpm) } : {}),
        params: cue.params,
      });
      if (!cue.everyTicks) break;
    }
  }
  if (steps.length > 32) throw new OpError('Motion cues expand to more than 32 steps; use fewer repetitions.');
  const animationId = s.animationId;
  const r = applyMotionPlan(project, {
    plan: { version: 1, slots, steps },
    binding,
    policy: animationId ? policy : 'new-animation',
    sceneId: s.sceneId,
    animationId,
    allowExtend,
    newAnimation: { name: `${s.name} motion` },
    ...(newId ? { newId } : {}),
  });
  const next = putSession(r.project, { ...sessionOf(r.project, s.id), animationId: r.animationId, motionCues: [] });
  return { project: next, summary: r.summary, animationId: r.animationId };
}

// ---------------------------------------------------------------- Reference integrity

export { pruneSessions } from './animationRefs';

/** The animation a session plays (null when none). */
export const sessionAnimation = (project: Project, s: AvSession) => (s.animationId ? animationOf(project, s.animationId) : null);
